// web/src/capabilities.ts — runtime capability & fallback matrix (M0.7).
//
// Break Glass is a zero-trust, client-side quorum app: confidentiality lives in
// the browser (REDESIGN-PLAN R11/R14). Before any crypto/ceremony flow runs we
// detect the four platform features it leans on and decide a *recommended*
// degradation per feature:
//
//   • WebCrypto SubtleCrypto — REQUIRED. Without it there is no client-side
//     Shamir/envelope crypto; the app must gate (unsupported browser).
//   • WebAuthn + PRF extension — OPTIONAL. PRF wraps the software private key at
//     rest on a hardware key (M2.4). Fallback: software-only key.
//   • WebRTC data channels — OPTIONAL. Direct peer shard delivery (M2.6/M3.2).
//     Fallback: encrypted store-and-forward via the DO.
//   • IndexedDB — REQUIRED. Holds the non-extractable private key + envelopes
//     (M1a.3). Without durable key storage the trust model breaks; gate.
//
// Detection is feature-presence based and never throws — a probe failure is a
// negative result, not an error. PRF support cannot be fully proven without a
// user gesture + authenticator, so we report API *availability* (the extension
// is recognised) and treat the actual wrap as a runtime fallback if it fails.

/** A single capability's detected support level. */
export type SupportLevel = 'supported' | 'unsupported' | 'unknown';

/** The recommended behaviour when a capability is missing. */
export type Fallback =
  | 'none' // capability is required; no fallback — gate the app
  | 'software-key' // WebAuthn-PRF missing → derive/hold the key in software
  | 'store-and-forward'; // WebRTC missing → relay ciphertext via the DO

/** One row of the capability matrix. */
export interface CapabilityResult {
  /** Whether the feature is present in this runtime. */
  level: SupportLevel;
  /** True if a missing/degraded result still leaves the app usable. */
  optional: boolean;
  /** What to do when this capability is not `supported`. */
  fallback: Fallback;
  /** Human-readable note for the support matrix / UI gate copy. */
  detail: string;
}

/** The set of individually-detected capability keys (matrix rows). */
export type CapabilityKey = 'webCrypto' | 'webAuthnPrf' | 'webRtc' | 'indexedDb';

/** The full detected capability set for the current runtime. */
export interface Capabilities {
  webCrypto: CapabilityResult;
  webAuthnPrf: CapabilityResult;
  webRtc: CapabilityResult;
  indexedDb: CapabilityResult;
  /**
   * True when every REQUIRED capability is `supported`. When false the app must
   * show the unsupported-browser gate instead of any crypto/ceremony flow.
   */
  usable: boolean;
  /** REQUIRED capabilities that are missing (drives the gate message). */
  missingRequired: ReadonlyArray<CapabilityKey>;
}

/** WebCrypto SubtleCrypto — required for all client-side crypto. */
function detectWebCrypto(): CapabilityResult {
  const subtle =
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.subtle !== 'undefined';
  // SubtleCrypto is only exposed in secure contexts (https / localhost).
  const secure = typeof isSecureContext === 'undefined' || isSecureContext;
  const ok = subtle && secure;
  return {
    level: ok ? 'supported' : 'unsupported',
    optional: false,
    fallback: 'none',
    detail: ok
      ? 'crypto.subtle available'
      : !subtle
        ? 'crypto.subtle missing — cannot perform client-side crypto'
        : 'not a secure context — crypto.subtle requires https or localhost',
  };
}

/**
 * WebAuthn + PRF extension. We can detect the WebAuthn API synchronously, but
 * PRF support depends on the authenticator and only resolves during a real
 * ceremony with a user gesture. We therefore report:
 *   • 'unsupported' when WebAuthn itself is absent (definitely no PRF),
 *   • 'unknown' when WebAuthn is present (PRF *may* be available; the wrap step
 *     falls back to software if the authenticator declines the extension).
 * Either non-'supported' result recommends the software-key fallback.
 */
function detectWebAuthnPrf(): CapabilityResult {
  const hasWebAuthn =
    typeof PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof navigator.credentials.create === 'function';
  if (!hasWebAuthn) {
    return {
      level: 'unsupported',
      optional: true,
      fallback: 'software-key',
      detail: 'WebAuthn unavailable — hardware-key wrap not possible',
    };
  }
  return {
    level: 'unknown',
    optional: true,
    fallback: 'software-key',
    detail:
      'WebAuthn present; PRF extension support resolves per-authenticator at ' +
      'enrollment — falls back to a software key if PRF is unavailable',
  };
}

/** WebRTC data channels — direct peer shard delivery; else store-and-forward. */
function detectWebRtc(): CapabilityResult {
  const pc =
    typeof RTCPeerConnection !== 'undefined' &&
    typeof RTCPeerConnection.prototype.createDataChannel === 'function';
  return {
    level: pc ? 'supported' : 'unsupported',
    optional: true,
    fallback: 'store-and-forward',
    detail: pc
      ? 'RTCPeerConnection + data channels available'
      : 'WebRTC unavailable — shards relayed via encrypted store-and-forward',
  };
}

/** IndexedDB — required to hold the non-extractable private key + envelopes. */
function detectIndexedDb(): CapabilityResult {
  const ok = typeof indexedDB !== 'undefined' && indexedDB !== null;
  return {
    level: ok ? 'supported' : 'unsupported',
    optional: false,
    fallback: 'none',
    detail: ok
      ? 'IndexedDB available for durable non-extractable key storage'
      : 'IndexedDB unavailable — cannot persist the device private key',
  };
}

/**
 * Detect all capabilities for the current runtime. Pure + synchronous + never
 * throws; safe to call at startup before rendering the app gate. Optional
 * capabilities never affect `usable`; only required capabilities gate the app.
 */
export function detectCapabilities(): Capabilities {
  const caps = {
    webCrypto: detectWebCrypto(),
    webAuthnPrf: detectWebAuthnPrf(),
    webRtc: detectWebRtc(),
    indexedDb: detectIndexedDb(),
  } satisfies Record<string, CapabilityResult>;

  const required: ReadonlyArray<CapabilityKey> = ['webCrypto', 'indexedDb'];
  const missingRequired = required.filter((k) => caps[k].level !== 'supported');

  return {
    ...caps,
    usable: missingRequired.length === 0,
    missingRequired,
  };
}

/** Human-readable label for a capability key (gate copy / matrix UI). */
export const CAPABILITY_LABELS: Record<CapabilityKey, string> = {
  webCrypto: 'Web Crypto (SubtleCrypto)',
  webAuthnPrf: 'WebAuthn PRF (hardware key)',
  webRtc: 'WebRTC data channels',
  indexedDb: 'IndexedDB',
};

/**
 * Build the user-facing gate message when the runtime is unusable. Returns null
 * when `usable` — callers render the app normally in that case.
 */
export function unsupportedMessage(caps: Capabilities): string | null {
  if (caps.usable) return null;
  const names = caps.missingRequired.map((k) => CAPABILITY_LABELS[k]).join(', ');
  return (
    `Break Glass needs ${names} to keep your secrets in your browser. ` +
    `Your browser does not support ${caps.missingRequired.length > 1 ? 'these features' : 'this feature'}. ` +
    `Please use a current version of Chrome, Edge, Firefox, or Safari over HTTPS.`
  );
}
